// src/pages/PackReaderPoetry.jsx
//
// Poetry-anthology student reader (slice PA1: PLACEHOLDER shell). Like the literature
// and skills readers this is a THIN WRAPPER over the shared PackReaderGrouped: it
// supplies the poetry grouping (lit_section items grouped by sectionKind via
// SECTION_ORDER) and the per-kind section renderers. All chrome -- nav, progress,
// entitlement gating, locked CTA, AI/Notes drawers, audio, StartHere, theme -- lives
// in the shared reader and is IDENTICAL to the other readers.
//
// PA1 scope: the `poem` sectionKind + its editor do NOT exist yet (poem sectionKind is
// PA2, editor PA3, comparison/matrix relabel PA4). So this slice ships the plumbing +
// a safe placeholder: any not-yet-built section kind renders the "next slice"
// placeholder, and an empty pack renders the shared reader's safe empty state (no crash
// -- groupByFixedOrder drops empty groups, so navGroups is [] and the reader shows its
// "No content available in this pack yet." panel). overview + matrix ARE valid lit
// section kinds today, so they render via the shared literature section renderers.
import React from 'react';
import PackReaderGrouped from './PackReaderGrouped.jsx';
import { groupByFixedOrder } from './readerGrouping.js';
import LitOverviewSection from './LitOverviewSection.jsx';
import LitMatrixSection from './LitMatrixSection.jsx';
import LitPoemSection from './LitPoemSection.jsx';
import { POETRY_OVERVIEW_LABELS } from '../config/poetryLabels.js';

// Reader overview labels: the shared poetry block labels + a poetry structure-tab hint
// (replaces the literature "A staged dramatic arc.").
const POETRY_OVERVIEW_READER_LABELS = {
    ...POETRY_OVERVIEW_LABELS,
    structureHint: 'Approaches for comparison and exam answers.',
};

// Section order + labels (student view). 'poem' is the anthology's per-poem sections
// (PA2+); 'matrix' is reused for cross-poem comparison (relabel is PA4).
const SECTION_ORDER = [
    { key: 'overview', label: 'Overview' },
    { key: 'poem', label: 'Poems' },
    { key: 'matrix', label: 'Comparison' },
];

function selectPoetrySections(items) {
    return (items || []).filter((it) => it && it.type === 'lit_section' && it.content && it.content.sectionKind);
}

// Render one lit_section by its sectionKind. poem -> the poetry section renderer (PA2);
// overview/matrix reuse the shared literature renderers. Any other kind falls back to the
// "next slice" placeholder, labelled by the active group.
function renderPoetrySection(item, group, done) {
    const kind = item.content.sectionKind;
    if (kind === 'poem') return <LitPoemSection content={item.content} done={done} />;
    // PA5: poetry-appropriate overview labels (Anthology Context / Anthology Map / Exam-
    // Comparison Strategy) -- data keys unchanged, no "Plot"/"Structure Diagram" leak.
    if (kind === 'overview') return <LitOverviewSection content={item.content} labels={POETRY_OVERVIEW_READER_LABELS} done={done} />;
    // PA4: reuse the matrix as a Poem x Theme comparison -- poem-axis labels, no "Character" leak.
    if (kind === 'matrix') return <LitMatrixSection content={item.content} titleFallback="Poem × Theme Comparison" groupTag="Comparison" done={done} />;
    return (
        <div className="border border-book-accent/20 bg-book-page/30 p-10 text-center">
            <div className="font-display text-book-accent text-xs mb-3 font-semibold">{group.label}</div>
            <p className="font-serif italic text-book-muted">This section renders in the next slice.</p>
        </div>
    );
}

const POETRY_CONFIG = {
    headerLabel: 'Poetry Anthology',
    itemType: 'lit_section',
    enableFlashcards: true,
    selectSections: selectPoetrySections,
    buildGroups: (sections) => groupByFixedOrder(sections, (it) => it.content.sectionKind, SECTION_ORDER),
    groupKeyOf: (item) => item.content.sectionKind,
    subListLabel: (item) => (item.content && item.content.title) || item.id,
    renderSection: renderPoetrySection,
};

export default function PackReaderPoetry() {
    return <PackReaderGrouped config={POETRY_CONFIG} />;
}
